import { Base } from "@app/controllers/base"
import L from "leaflet"
import { TILES, addTileControl, applyTile } from "./map_tile_control"
import "leaflet.markercluster"

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconUrl:       new URL("leaflet/dist/images/marker-icon.png",    import.meta.url).href,
  iconRetinaUrl: new URL("leaflet/dist/images/marker-icon-2x.png", import.meta.url).href,
  shadowUrl:     new URL("leaflet/dist/images/marker-shadow.png",  import.meta.url).href,
})

export default class extends Base {
  static values = {
    center:      { type: Array,  default: [-8.4095, 115.1889] },
    zoom:        { type: Number, default: 14 },
    dronePoints: { type: Array,  default: [] },
    venueName:   { type: String, default: "" },
    venueCenter: { type: Array,  default: [] },
  }

  static targets = ["mapContainer", "dialog", "modalMapContainer"]

  connect() {
    this._smallMap     = null
    this._modalMap     = null
    this._modalCluster = null
    this._markerRefs   = []
    this._activeItem   = null
    this._initSmallMap()
  }

  disconnect() {
    this._smallMap?.remove()
    this._modalMap?.remove()
    this._smallMap = this._modalMap = null
  }

  expand() {
    this.dialogTarget.showModal()
    requestAnimationFrame(() => {
      if (!this._modalMap) {
        this._markerRefs   = []
        this._modalMap     = this._buildMap(this.modalMapContainerTarget, true)
        this._modalCluster = this._addMarkers(this._modalMap, this._markerRefs)
        this._fitBounds(this._modalMap)
        addTileControl(this._modalMap, this._defaultTile())
      } else {
        this._modalMap.invalidateSize()
      }
    })
  }

  close() {
    this.dialogTarget.close()
  }

  closeOnBackdrop(event) {
    if (event.target === this.dialogTarget) this.dialogTarget.close()
  }

  flyTo(event) {
    const btn    = event.currentTarget
    const index  = parseInt(btn.dataset.index, 10)
    const dp     = this.dronePointsValue[index]
    const marker = this._markerRefs[index]
    if (!dp || !this._modalMap || !this._modalCluster) return

    this._activeItem?.classList.remove("vmds-item--active")
    this._activeItem = btn
    btn.classList.add("vmds-item--active")
    btn.scrollIntoView({ block: "nearest", behavior: "smooth" })

    this._modalCluster.zoomToShowLayer(marker, () => marker.openPopup())
  }

  // ── private ─────────────────────────────────────────────────────────────────

  _initSmallMap() {
    this._smallMap = this._buildMap(this.mapContainerTarget, false)
    this._addMarkers(this._smallMap, null)
    this._fitBounds(this._smallMap)
    addTileControl(this._smallMap, this._defaultTile())
    requestAnimationFrame(() => this._smallMap?.invalidateSize())
    setTimeout(() => this._smallMap?.invalidateSize(), 150)
  }

  _buildMap(container, scrollZoom) {
    const map = L.map(container, { zoomControl: true, gestureHandling: true, maxZoom: 19 })
    map.setView(this.centerValue, this.zoomValue)
    return map
  }

  _addMarkers(map, markerRefs) {
    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 50,
      iconCreateFunction: this._clusterIcon,
    })

    const vc = this.venueCenterValue
    if (vc.length === 2) {
      L.marker(vc, { icon: this._venueIcon() })
        .addTo(map)
        .bindPopup(
          `<div style="font-size:13px;font-weight:700;color:#1f2a44">${this.venueNameValue}</div>` +
          `<div style="font-size:11px;color:#6b7280;margin-top:2px">Venue</div>`,
          { maxWidth: 200 }
        )
    }

    this.dronePointsValue.forEach((dp, i) => {
      const lat = parseFloat(dp.lat)
      const lon = parseFloat(dp.lon)
      if (isNaN(lat) || isNaN(lon)) return
      const marker = L.marker([lat, lon], { icon: this._droneIcon() })
        .bindPopup(
          `<div style="font-size:13px;font-weight:700;color:#1f2a44">${dp.name}</div>` +
          `<div style="font-size:11px;color:#6b7280;margin-top:2px">Launch Point</div>`,
          { maxWidth: 200 }
        )
      if (markerRefs) markerRefs[i] = marker
      cluster.addLayer(marker)
    })

    map.addLayer(cluster)
    return cluster
  }

  _fitBounds(map) {
    const pts = this.dronePointsValue
      .filter(dp => !isNaN(parseFloat(dp.lat)) && !isNaN(parseFloat(dp.lon)))
      .map(dp => [parseFloat(dp.lat), parseFloat(dp.lon)])

    const vc = this.venueCenterValue
    if (vc.length === 2) pts.push(vc)

    if (pts.length >= 2) {
      map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 14 })
    } else if (pts.length === 1) {
      map.setView(pts[0], Math.min(this.zoomValue, 14))
    }
  }

  _defaultTile() {
    return document.documentElement.getAttribute("data-bs-theme") === "dark" ||
           document.documentElement.classList.contains("dark") ? "dark" : "street"
  }

  _clusterIcon(cluster) {
    const count = cluster.getChildCount()
    return L.divIcon({
      className: "",
      html: `<div style="
        width:36px;height:36px;border-radius:50%;
        background:#1f2a44;border:3px solid #fff;
        box-shadow:0 3px 10px rgba(0,0,0,.4);
        display:flex;align-items:center;justify-content:center;
        font-size:12px;font-weight:700;color:#fff;letter-spacing:-0.5px;
      ">${count}</div>`,
      iconSize: [36, 36], iconAnchor: [18, 18],
    })
  }

  _venueIcon() {
    return L.divIcon({
      className: "",
      html: `<div style="
        width:36px;height:36px;border-radius:50%;
        background:#c9a84c;border:3px solid #fff;
        box-shadow:0 3px 10px rgba(0,0,0,.4);
        display:flex;align-items:center;justify-content:center;
      "><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg></div>`,
      iconSize: [36, 36], iconAnchor: [18, 36], popupAnchor: [0, -38],
    })
  }

  _droneIcon() {
    return L.divIcon({
      className: "",
      html: `<div style="
        width:30px;height:30px;border-radius:50%;
        background:#1f2a44;border:2.5px solid #fff;
        box-shadow:0 2px 8px rgba(0,0,0,.4);
        display:flex;align-items:center;justify-content:center;
      "><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
      </svg></div>`,
      iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -18],
    })
  }
}
